<template>
  <section v-if="!useV2026LineAudienceSettings" class="p-3">
    <h4>จัดการ Line Audience Group</h4>
    <div class="row pt-3">
      <div class="col-lg-4 col-12">
        <h6>Tag คุณลูกค้า</h6>
        <v-select id="tags"
                  placeholder="เลือกแท็ก"
                  name="tags"
                  label="name"
                  :options="tags"
                  @input="onChange"
                  v-if="tags">
          <template #search="{attributes, events}">
            <input
              class="vs__search"
              v-bind="attributes"
              v-on="events"/>
          </template>
          <template v-slot:option="option">
            <span :style="`color: ${option.color}`">{{ option.name }}</span>
          </template>
        </v-select>

        <section>
          <div class="card mt-3" v-if="audienceGroup">
            <div class="card-body">
              <h5>Line Audience Group Info</h5>
              <ul class="list-unstyled" style="line-height: 1.65rem;">
                <li>Id: {{ selectedTag.line_audience_group_id }}</li>
                <li>สถานะ:
                  <span class="text-danger" v-if="audienceGroup.status.toLowerCase() === 'expired'">
                    {{ audienceGroup.status }}
                  </span>
                  <span class="text-success" v-else>
                    {{ audienceGroup.status }}
                  </span>
                </li>
                <li>วันหมดอายุ: {{ convertTimeStamp(audienceGroup.expireTimestamp) }}</li>
              </ul>
              <div class="text-danger">*กรณีที่สถานะหมดอายุ (Expired) จะเป็นการสร้าง Line Audience Group ใหม่</div>
              <div class="pt-2">
                <button class="btn btn-block btn-primary" @click="addLineAudiences()">เพิ่มผู้ใช้เข้า Line
                  Audience</button>
              </div>
            </div>
          </div>
        </section>
      </div>
      <div class="col-lg-8">
        <div class="card mt-3" v-if="conversations.length > 0">
          <div class="card-body">
            <h5>Customers ({{ conversations.length }})</h5>
            <div class="d-flex align-items-center text-small py-2"
                 v-for="(conversation, index) in conversations" :key="index">
              <div>
                <img v-lazy="conversation.customer.profile_picture" alt="" class="rounded-circle img-circle" width="48">
              </div>
              <div class="pl-3">
                <div>{{ index + 1 }}. {{ conversation.customer.name }}</div>
                <div>Line ID: {{ conversation.customer.lid }}</div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </section>

  <section v-else class="v2026-la" :class="{ 'is-narrow': isNarrow }">
    <h4 class="v2026-la-title">จัดการ Line Audience Group</h4>

    <div class="v2026-la-toolbar">
      <div class="v2026-la-field">
        <label class="v2026-la-label" for="v2026-la-tag">Tag คุณลูกค้า</label>
        <select id="v2026-la-tag"
                class="v2026-la-select"
                v-model="pickedTagId"
                :disabled="isLoadingTags"
                @change="onPickV2026">
          <option value="">เลือกแท็ก</option>
          <option v-for="tag in tagRows"
                  :key="tag.id"
                  :value="String(tag.id)"
                  :style="{ color: tag.color }">{{ tag.name }}</option>
        </select>
      </div>
      <LoadingV2026 v-if="showToolbarLoading" :label="toolbarLoadingLabel"/>
    </div>

    <div v-if="isLoadingTags && !tagRows.length" class="v2026-la-loading" role="status">
      <LoadingV2026 label="กำลังโหลด"/>
    </div>

    <div v-else-if="loadError && !tagRows.length" class="v2026-la-empty" role="status">
      <span class="v2026-la-empty__ico" aria-hidden="true"></span>
      <div class="v2026-la-empty__title">โหลดแท็กไม่สำเร็จ</div>
      <div class="v2026-la-empty__msg">{{ loadError }}</div>
      <button type="button" class="v2026-la-btn-ghost v2026-la-empty__act" @click="loadTagsV2026">ลองใหม่</button>
    </div>

    <div v-else-if="!pickedTagId" class="v2026-la-empty" role="status">
      <span class="v2026-la-empty__ico" aria-hidden="true"></span>
      <div class="v2026-la-empty__title">เลือกแท็กคุณลูกค้า</div>
      <div class="v2026-la-empty__msg">เลือกแท็กที่เป็น Line Audience Group เพื่อดูข้อมูลกลุ่มและรายชื่อลูกค้า</div>
    </div>

    <div v-else ref="laGrid" class="v2026-la-grid">
      <aside class="v2026-la-side">
        <div v-if="isLoadingAudience" class="v2026-la-card v2026-la-card--loading" role="status">
          <LoadingV2026 label="กำลังโหลดกลุ่ม"/>
        </div>

        <div v-else-if="audienceLoadError" class="v2026-la-card" role="status">
          <div class="v2026-la-card__title">โหลดกลุ่มไม่สำเร็จ</div>
          <p class="v2026-la-hint">{{ audienceLoadError }}</p>
          <button type="button" class="v2026-la-btn-ghost" @click="retryAudienceV2026">ลองใหม่</button>
        </div>

        <div v-else-if="audienceGroup && selectedTag" class="v2026-la-card">
          <div class="v2026-la-card__title">Line Audience Group Info</div>
          <dl class="v2026-la-dl">
            <div class="v2026-la-dl__row">
              <dt>Id</dt>
              <dd>{{ selectedTag.line_audience_group_id }}</dd>
            </div>
            <div class="v2026-la-dl__row">
              <dt>สถานะ</dt>
              <dd>
                <span class="v2026-la-badge" :class="isExpired ? 'is-off' : 'is-on'">
                  <span class="v2026-la-badge__dot" aria-hidden="true"></span>
                  {{ audienceGroup.status }}
                </span>
              </dd>
            </div>
            <div class="v2026-la-dl__row">
              <dt>วันหมดอายุ</dt>
              <dd>{{ convertTimeStamp(audienceGroup.expireTimestamp) }}</dd>
            </div>
          </dl>
          <p class="v2026-la-note">*กรณีที่สถานะหมดอายุ (Expired) จะเป็นการสร้าง Line Audience Group ใหม่</p>
          <button type="button"
                  class="v2026-la-btn-primary v2026-la-btn-block"
                  :disabled="isSaving"
                  @click="openAddV2026">เพิ่มผู้ใช้เข้า Line Audience</button>
        </div>

        <div v-else class="v2026-la-card v2026-la-card--muted" role="status">
          <div class="v2026-la-card__title">ยังไม่มี Line Audience Group</div>
          <p class="v2026-la-hint">แท็กนี้ยังไม่มีกลุ่ม หรือยังไม่ใช่แท็ก Line Audience Group</p>
        </div>
      </aside>

      <div class="v2026-la-main" :aria-busy="isLoadingCustomers ? 'true' : 'false'">
        <div v-if="isLoadingCustomers && !customerRows.length" class="v2026-la-loading" role="status">
          <LoadingV2026 label="กำลังโหลด"/>
        </div>

        <div v-else-if="customerLoadError && !customerRows.length" class="v2026-la-empty" role="status">
          <span class="v2026-la-empty__ico" aria-hidden="true"></span>
          <div class="v2026-la-empty__title">โหลดลูกค้าไม่สำเร็จ</div>
          <div class="v2026-la-empty__msg">{{ customerLoadError }}</div>
          <button type="button" class="v2026-la-btn-ghost v2026-la-empty__act" @click="retryCustomersV2026">ลองใหม่</button>
        </div>

        <div v-else-if="!customerRows.length" class="v2026-la-empty" role="status">
          <span class="v2026-la-empty__ico" aria-hidden="true"></span>
          <div class="v2026-la-empty__title">ยังไม่มีลูกค้าในแท็กนี้</div>
          <div class="v2026-la-empty__msg">ลูกค้าที่ติดแท็กนี้จะปรากฏที่นี่</div>
        </div>

        <div v-else>
          <div class="v2026-la-list__title">Customers ({{ customerRows.length }})</div>
          <div class="v2026-la-list">
          <div class="v2026-la-head" aria-hidden="true">
            <span>#</span>
            <span>ลูกค้า</span>
            <span v-if="!isNarrow">Line ID</span>
          </div>
          <div v-for="(conversation, index) in customerRows"
               :key="conversation.id || index"
               class="v2026-la-row">
            <span class="v2026-la-idx">{{ index + 1 }}</span>
            <div class="v2026-la-person">
              <img v-if="conversation.customer"
                   v-lazy="conversation.customer.profile_picture"
                   alt=""
                   class="v2026-la-avatar">
              <div class="v2026-la-person__text">
                <div class="v2026-la-person__name" :title="customerName(conversation)">
                  {{ customerName(conversation) }}
                </div>
                <div v-if="isNarrow" class="v2026-la-person__lid">Line ID: {{ customerLid(conversation) }}</div>
              </div>
            </div>
            <span v-if="!isNarrow" class="v2026-la-lid">{{ customerLid(conversation) }}</span>
          </div>
          </div>
        </div>
      </div>
    </div>

    <BaseConfirm :open="confirm.open"
                 :title="'เพิ่มผู้ใช้เข้า Line Audience Group “' + (confirm.name || '') + '” ?'"
                 :message="confirm.message"
                 confirmLabel="เพิ่ม"
                 cancelLabel="ยกเลิก"
                 variant="primary"
                 :busy="confirm.busy"
                 @confirm="acceptAddV2026"
                 @cancel="cancelAddV2026"/>

    <BaseToast :open="toast.open"
               :variant="toast.variant"
               :title="toast.title"
               :message="toast.message"
               :autoHideDelay="toast.variant === 'danger' ? 5000 : 2500"
               @close="toast.open = false"/>
  </section>
</template>

<script>
import axios from 'axios'
import { mapActions, mapState } from 'vuex'
import moment from 'moment'
import BaseConfirm from '../components/coversation/BaseConfirm'
import BaseToast from '../components/coversation/BaseToast'
import LoadingV2026 from '../components/coversation/LoadingV2026'

export default {
  components: {
    BaseConfirm,
    BaseToast,
    LoadingV2026
  },
  data () {
    return {
      queryParams: {
        include_count: false,
        customer_type: 'customer',
        system: 0
      },
      audienceId: null,
      selectedTag: null,
      audienceGroup: null,
      // version2026: FALSE restores today's bootstrap page verbatim — the v-select (including the
      // crash on a cleared value and the is_line_audience_group gate that leaves selectedTag on
      // the previous audience tag), the two .card columns, and every $swal. Flipping this boolean
      // is the entire rollback.
      useV2026LineAudienceSettings: true,
      pickedTagId: '',
      isNarrow: false,
      isLoadingTags: false,
      isLoadingAudience: false,
      isLoadingCustomers: false,
      isSaving: false,
      loadError: '',
      audienceLoadError: '',
      customerLoadError: '',
      confirm: {
        open: false,
        name: '',
        message: '',
        busy: false
      },
      toast: {
        open: false,
        variant: 'success',
        title: '',
        message: ''
      }
    }
  },
  computed: {
    ...mapState('tag', ['tags']),
    ...mapState('conversation', ['conversations']),
    tagRows () {
      return this.tags || []
    },
    customerRows () {
      return this.conversations || []
    },
    isExpired () {
      return !!(this.audienceGroup && String(this.audienceGroup.status).toLowerCase() === 'expired')
    },
    showToolbarLoading () {
      return this.isLoadingTags && this.tagRows.length > 0
    },
    toolbarLoadingLabel () {
      return 'กำลังโหลด'
    }
  },
  methods: {
    ...mapActions('tag', ['fetchTags']),
    ...mapActions('conversation', ['fetchConversationCustomers']),
    convertTimeStamp (timestamp) {
      return moment.unix(timestamp).format('DD/MM/YYYY HH:mm')
    },
    customerName (conversation) {
      return (conversation.customer && conversation.customer.name) || ''
    },
    customerLid (conversation) {
      return (conversation.customer && conversation.customer.lid) || '—'
    },
    errorTextV2026 (err) {
      const body = err && err.response && err.response.data
      return (body && body.error) || (err && err.message) || 'เกิดข้อผิดพลาด'
    },
    showToast (variant, title, message) {
      this.toast = { open: true, variant, title, message }
    },
    async loadTagsV2026 () {
      this.queryParams.customer_type = 'customer'
      this.isLoadingTags = true
      this.loadError = ''
      try {
        const { data } = await axios.get('/tags', { params: this.queryParams })
        this.$store.commit('tag/SET_TAGS', (data && data.length) ? data : [])
      } catch (err) {
        this.loadError = this.errorTextV2026(err)
        this.showToast('danger', 'โหลดแท็กไม่สำเร็จ', this.loadError)
      } finally {
        this.isLoadingTags = false
        this.$nextTick(this.attachObserverV2026)
      }
    },
    async onPickV2026 () {
      // A cleared native select is '', not the object v-select emitted. The flag-OFF @input
      // crashes on tag.is_line_audience_group when that happens; the ON path just resets.
      if (!this.pickedTagId) {
        this.selectedTag = null
        this.audienceGroup = null
        this.audienceLoadError = ''
        this.customerLoadError = ''
        this.$store.commit('conversation/SET_CONVERSATIONS', [])
        return
      }
      const tag = this.tagRows.find(item => String(item.id) === String(this.pickedTagId))
      if (!tag) return
      await this.applyTagV2026(tag)
      this.$nextTick(this.attachObserverV2026)
    },
    async applyTagV2026 (tag) {
      this.customerLoadError = ''
      this.audienceLoadError = ''
      // Same gate as onChange: only an audience tag writes selectedTag and fetches customers.
      // A non-audience pick leaves the previous group/list on screen, then re-checks that id.
      if (tag.is_line_audience_group === 1) {
        this.selectedTag = tag
        await this.loadCustomersV2026(tag.id)
      }
      const audienceGroupId = (this.selectedTag && this.selectedTag.line_audience_group_id)
        ? this.selectedTag.line_audience_group_id
        : null
      await this.checkLineAudienceV2026(audienceGroupId)
    },
    async loadCustomersV2026 (tagId) {
      this.isLoadingCustomers = true
      this.customerLoadError = ''
      this.$store.commit('conversation/SET_CONVERSATIONS', [])
      try {
        // axios directly, never conversation/fetchConversationCustomers: that action $swal's.
        const { data } = await axios.get('/conversations/customers', { params: { tag_id: tagId } })
        this.$store.commit('conversation/SET_CONVERSATIONS', (data && data.conversations) || [])
      } catch (err) {
        this.customerLoadError = this.errorTextV2026(err)
        this.showToast('danger', 'โหลดลูกค้าไม่สำเร็จ', this.customerLoadError)
      } finally {
        this.isLoadingCustomers = false
      }
    },
    async checkLineAudienceV2026 (audienceGroupId) {
      this.audienceGroup = null
      this.audienceLoadError = ''
      if (!audienceGroupId) return
      this.isLoadingAudience = true
      try {
        const { data } = await axios.get('/line/check-audience', { params: { audience_group_id: audienceGroupId } })
        if (data) this.audienceGroup = data.audienceGroup
      } catch (err) {
        this.audienceLoadError = this.errorTextV2026(err)
        this.showToast('danger', 'โหลดกลุ่มไม่สำเร็จ', this.audienceLoadError)
      } finally {
        this.isLoadingAudience = false
      }
    },
    retryAudienceV2026 () {
      const audienceGroupId = (this.selectedTag && this.selectedTag.line_audience_group_id)
        ? this.selectedTag.line_audience_group_id
        : null
      return this.checkLineAudienceV2026(audienceGroupId)
    },
    retryCustomersV2026 () {
      if (!this.selectedTag) return
      return this.loadCustomersV2026(this.selectedTag.id)
    },
    openAddV2026 () {
      if (!this.selectedTag) return
      this.confirm = {
        open: true,
        name: this.selectedTag.name,
        message: this.isExpired
          ? 'สถานะหมดอายุ จะสร้าง Line Audience Group ใหม่ จากนั้นเพิ่มผู้ใช้ที่มี Line ID เข้ากลุ่ม'
          : 'จะเพิ่มผู้ใช้ที่มี Line ID ในรายการนี้เข้ากลุ่ม',
        busy: false
      }
    },
    cancelAddV2026 () {
      this.confirm = { open: false, name: '', message: '', busy: false }
    },
    async acceptAddV2026 () {
      if (!this.selectedTag || this.confirm.busy) return
      this.confirm.busy = true
      this.isSaving = true
      try {
        const audiences = []
        for (let conversation of this.customerRows) {
          if (conversation.customer && conversation.customer.lid) {
            audiences.push({ id: conversation.customer.lid })
          }
        }
        let audienceGroupId = (this.selectedTag && this.selectedTag.line_audience_group_id)
          ? this.selectedTag.line_audience_group_id
          : null
        if (this.audienceGroup && String(this.audienceGroup.status).toLowerCase() === 'expired') {
          audienceGroupId = null
        }
        // `if (audiences)` is always true for an array — same as the flag-OFF post.
        const submitData = {
          tagId: this.selectedTag.id,
          description: this.selectedTag.name,
          audiences: audiences
        }
        if (audienceGroupId) submitData.audienceGroupId = audienceGroupId
        const res = await axios.post('/line/add-audiences', submitData)
        if (res.status === 200) {
          const data = res.data
          if (data.action === 'add' && data.audience_group) {
            await this.checkLineAudienceV2026(data.audience_group.audienceGroupId)
          }
          this.showToast('success', 'เพิ่มข้อมูลสำเร็จ', '')
        }
        this.cancelAddV2026()
      } catch (err) {
        this.confirm.busy = false
        this.showToast('danger', 'เพิ่มผู้ใช้ไม่สำเร็จ', this.errorTextV2026(err))
      } finally {
        this.isSaving = false
      }
    },
    measureLayout () {
      const el = this.$refs.laGrid
      if (el) this.isNarrow = el.clientWidth < 720
    },
    attachObserverV2026 () {
      this.measureLayout()
      if (typeof ResizeObserver === 'undefined') return
      const el = this.$refs.laGrid
      if (!el || el === this._roTarget) return
      if (this._ro) this._ro.disconnect()
      this._ro = new ResizeObserver(this.measureLayout)
      this._ro.observe(el)
      this._roTarget = el
    },
    async onChange (tag) {
      if (tag.is_line_audience_group === 1) {
        this.selectedTag = tag
        const params = {
          tag_id: this.selectedTag.id
        }
        await this.fetchConversationCustomers(params)
      }
      const audienceGroupId = (this.selectedTag && this.selectedTag.line_audience_group_id) ? this.selectedTag.line_audience_group_id : null
      await this.checkLineAudience(audienceGroupId)
    },
    async loadTags (customerType) {
      if (this.useV2026LineAudienceSettings) {
        await this.loadTagsV2026()
        return
      }
      this.queryParams.customer_type = customerType
      await this.fetchTags(this.queryParams)
    },
    async checkLineAudience (audienceGroupId) {
      this.audienceGroup = null
      if (audienceGroupId) {
        const url = '/line/check-audience'
        const params = { audience_group_id: audienceGroupId }
        const { data } = await axios.get(url, { params })
        if (data) {
          this.audienceGroup = data.audienceGroup
        }
      }
    },
    async addLineAudiences () {
      this.$swal({
        text: `คุณต้องเพิ่มผู้ใช้เข้า Line Audience Group: ${this.selectedTag.name} ใช่หรือไม่`,
        showCancelButton: true,
        confirmButtonText: 'ใช่',
        cancelButtonText: 'ไม่'
      }).then(async (result) => {
        if (result.isConfirmed) {
          const audiences = []
          for (let conversation of this.conversations) {
            if (conversation.customer.lid) {
              audiences.push({ id: conversation.customer.lid })
            }
          }
          let audienceGroupId = (this.selectedTag && this.selectedTag.line_audience_group_id) ? this.selectedTag.line_audience_group_id : null
          if (this.audienceGroup && this.audienceGroup.status.toLowerCase() === 'expired') {
            audienceGroupId = null
          }
          if (audiences) {
            const url = '/line/add-audiences'
            const submitData = {
              tagId: this.selectedTag.id,
              description: this.selectedTag.name,
              audiences: audiences
            }
            if (audienceGroupId) {
              submitData.audienceGroupId = audienceGroupId
            }
            console.log('check submitData', submitData)
            const res = await axios.post(url, submitData)
            if (res.status === 200) {
              const data = res.data
              if (data.action === 'add' && data.audience_group) {
                await this.checkLineAudience(data.audience_group.audienceGroupId)
              }
              this.$swal({ text: 'เพิ่มข้อมูลสำเร็จ', icon: 'success' })
            }
          }
        }
      })
    }
  },
  async mounted () {
    this.$store.commit('conversation/SET_CONVERSATIONS', [])
    await this.loadTags('customer')
    if (!this.useV2026LineAudienceSettings) return
    this.$nextTick(this.attachObserverV2026)
    window.addEventListener('resize', this.measureLayout)
  },
  beforeDestroy () {
    if (!this.useV2026LineAudienceSettings) return
    if (this._ro) {
      this._ro.disconnect()
      this._ro = null
      this._roTarget = null
    }
    window.removeEventListener('resize', this.measureLayout)
  }
}
</script>

<style lang="scss" scoped>
/* Only the ten --v2026-color-* tokens declared on .v2026-shell resolve under /settings, so every
   grey below is hard-coded on purpose. */
.v2026-la {
  padding: 16px 16px 48px;
  background: #ffffff;
}

.v2026-la-title {
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 600;
  color: #212529;
}

.v2026-la-toolbar {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  margin: 12px 0 8px;
}

.v2026-la-field {
  min-width: 0;
  width: 320px;
  max-width: 100%;

  .is-narrow & { width: 100%; }
}

.v2026-la-label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #343a40;
}

.v2026-la-select {
  width: 100%;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #E4E7EC;
  border-radius: 8px;
  background: #ffffff;
  font-size: 14px;
  color: #212529;

  &:focus {
    outline: none;
    border-color: var(--v2026-color-main);
    box-shadow: 0 0 0 3px rgba(49, 193, 215, .30);
  }
  &:disabled { opacity: .5; }
}

.v2026-la-grid {
  display: grid;
  grid-template-columns: minmax(260px, 340px) minmax(0, 1fr);
  gap: 24px;
  align-items: start;
  padding-top: 4px;

  .is-narrow & {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
}

.v2026-la-card {
  padding: 16px;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  background: #ffffff;

  &--loading {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 120px;
  }

  &--muted { background: #F9FAFB; }
}

.v2026-la-card__title {
  margin: 0 0 12px;
  font-size: 15px;
  font-weight: 600;
  color: #212529;
}

.v2026-la-dl {
  margin: 0;
}

.v2026-la-dl__row {
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  column-gap: 12px;
  align-items: center;
  min-height: 32px;
  font-size: 14px;

  dt {
    margin: 0;
    font-weight: 400;
    color: #66696C;
  }
  dd {
    margin: 0;
    color: #212529;
    word-break: break-word;
  }
}

.v2026-la-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  white-space: nowrap;

  &__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }

  &.is-on { background: #ECFDF5; color: #15794F; }
  &.is-off { background: #FEF2F2; color: #E5484D; }
}

.v2026-la-note {
  margin: 12px 0 0;
  font-size: 13px;
  color: #E5484D;
}

.v2026-la-hint {
  margin: 0 0 12px;
  font-size: 13px;
  color: #66696C;
}

.v2026-la-btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border: 0;
  border-radius: 8px;
  background: var(--v2026-color-main);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) { filter: brightness(.94); }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }
  &:disabled { opacity: .5; cursor: default; }
}

.v2026-la-btn-block {
  width: 100%;
  margin-top: 16px;
}

.v2026-la-btn-ghost {
  height: 36px;
  padding: 0 14px;
  border: 1px solid #E4E7EC;
  border-radius: 8px;
  background: #ffffff;
  font-size: 14px;
  color: #212529;
  cursor: pointer;
  white-space: nowrap;

  &:hover { background: var(--v2026-color-hover-bg); }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(49, 193, 215, .30); }
}

.v2026-la-list {
  --la-cols: 40px minmax(0, 1fr) minmax(140px, 220px);
  border-top: 1px solid #e9ecef;

  .is-narrow & { --la-cols: 40px minmax(0, 1fr); }
}

.v2026-la-list__title {
  padding: 8px 8px 12px;
  font-size: 15px;
  font-weight: 600;
  color: #212529;
}

.v2026-la-head {
  position: sticky;
  top: 0;
  z-index: 2;
  display: grid;
  grid-template-columns: var(--la-cols);
  column-gap: 12px;
  align-items: center;
  height: 40px;
  padding: 0 8px;
  background: #ffffff;
  border-bottom: 1px solid #e9ecef;
  font-size: 12px;
  font-weight: 600;
  color: #66696C;
}

.v2026-la-row {
  display: grid;
  grid-template-columns: var(--la-cols);
  column-gap: 12px;
  align-items: center;
  min-height: 56px;
  padding: 8px;
  border-bottom: 1px solid #e9ecef;

  &:last-child { border-bottom: 0; }
}

.v2026-la-idx {
  font-size: 13px;
  color: #66696C;
  text-align: center;
}

.v2026-la-person {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.v2026-la-avatar {
  width: 40px;
  height: 40px;
  flex: 0 0 auto;
  border-radius: 50%;
  object-fit: cover;
  background: #F2F4F7;
}

.v2026-la-person__text { min-width: 0; }

.v2026-la-person__name {
  font-size: 14px;
  color: #212529;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.v2026-la-person__lid,
.v2026-la-lid {
  font-size: 13px;
  color: #66696C;
  word-break: break-all;
}

.v2026-la-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 224px;
}

.v2026-la-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  padding: 56px 16px;
}

.v2026-la-empty__ico {
  width: 40px;
  height: 40px;
  background-color: #66696C;
  opacity: .55;
  -webkit-mask: url('~@/assets/icons2026/line.svg') center / contain no-repeat;
          mask: url('~@/assets/icons2026/line.svg') center / contain no-repeat;
}

.v2026-la-empty__title {
  font-size: 15px;
  font-weight: 600;
  color: #212529;
}

.v2026-la-empty__msg {
  font-size: 13px;
  color: #66696C;
  max-width: 420px;
}

.v2026-la-empty__act { margin-top: 8px; }
</style>
